import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const SERVICES = ["General Enquiry","Asbestos","Fire Safety","Water Hygiene","Compliance & Building Safety","Remedial & Construction"];
const SECTORS = ["","Property Management","Block Management","Schools & Education","Care Homes","Commercial Real Estate","Industrial","Other"];

export default function ContactForm({ defaultService = "General Enquiry", variant = "light" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: defaultService, sector: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/enquiries", form);
      // GA4 conversion — fires only on a real 2xx so we never inflate the count
      trackEvent("generate_lead", {
        service: form.service || "General",
        sector: form.sector || "unspecified",
        method: "website_form",
        ref_no: res?.data?.ref_no || null,
      });
      setSubmitted(true);
      toast.success("Enquiry received. A consultant will contact you within 1 working day.");
      setForm({ name: "", email: "", phone: "", company: "", service: defaultService, sector: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const dark = variant === "dark";
  const inputCls = dark
    ? "w-full bg-white/5 border border-white/15 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#3FBAB0] transition-colors"
    : "w-full bg-white border border-slate-300 px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#1FA59A] transition-colors";
  const labelCls = `text-[10px] uppercase tracking-[0.2em] mb-2 block ${dark ? "text-white/60" : "text-slate-500"}`;

  if (submitted) {
    return (
      <div className={`p-8 border ${dark ? "border-white/15 bg-white/5 text-white" : "border-slate-300 bg-slate-50 text-slate-900"}`} data-testid="enquiry-success">
        <h3 className="font-display text-2xl font-semibold mb-3">Enquiry received</h3>
        <p className={dark ? "text-white/70" : "text-slate-600"}>Thank you. A OEC consultant will contact you within one working day. For urgent matters call 03456 868 868.</p>
        <button onClick={() => setSubmitted(false)} className={`mt-6 text-sm font-semibold underline ${dark ? "text-white" : "text-[#1FA59A]"}`} data-testid="submit-another">Submit another enquiry</button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="enquiry-form">
      <div>
        <label className={labelCls}>Name *</label>
        <input required value={form.name} onChange={set("name")} className={inputCls} data-testid="enquiry-name" />
      </div>
      <div>
        <label className={labelCls}>Email *</label>
        <input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="enquiry-email" />
      </div>
      <div>
        <label className={labelCls}>Phone</label>
        <input value={form.phone} onChange={set("phone")} className={inputCls} data-testid="enquiry-phone" />
      </div>
      <div>
        <label className={labelCls}>Company</label>
        <input value={form.company} onChange={set("company")} className={inputCls} data-testid="enquiry-company" />
      </div>
      <div>
        <label className={labelCls}>Service</label>
        <select value={form.service} onChange={set("service")} className={inputCls} data-testid="enquiry-service">
          {SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label className={labelCls}>Sector</label>
        <select value={form.sector} onChange={set("sector")} className={inputCls} data-testid="enquiry-sector">
          {SECTORS.map(s => <option key={s} value={s}>{s || "Select sector"}</option>)}
        </select>
      </div>
      <div className="md:col-span-2">
        <label className={labelCls}>Message *</label>
        <textarea required rows={5} value={form.message} onChange={set("message")} className={inputCls} data-testid="enquiry-message" />
      </div>
      <div className="md:col-span-2">
        <button type="submit" disabled={loading} className="gs-gradient text-white font-bold px-8 py-4 flex items-center gap-3 hover:opacity-90 transition-opacity disabled:opacity-50" data-testid="enquiry-submit">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {loading ? "Sending…" : "Submit Enquiry"}
        </button>
      </div>
    </form>
  );
}
